/**
 * Message rendering for the Ask panel.
 *
 * Presentational only — it reads a conversation and paints it. Nothing here starts,
 * streams, or cancels a turn; that is the runner's job (D3). Keeping the two apart means
 * the streaming state machine can be reasoned about without a component in the way, and
 * this file can be looked at to answer "what does an errored / cancelled / uncovered turn
 * look like" without reading any async code.
 *
 * Answers are rendered as PLAIN TEXT, deliberately. The markdown renderer lives in a
 * lazily-loaded chunk that Epic B went to some trouble to keep out of the eager graph
 * (see vite.config.ts), and the Ask panel is mounted in Layout — on every authenticated
 * page. Importing the renderer here would statically anchor that chunk and undo the
 * split. If rich answers are wanted later, the renderer must be reached through a
 * dynamic import, not a plain one.
 */
import React, { useEffect, useRef } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import {
  AlertCircleIcon,
  BookOpenIcon,
  ExternalLinkIcon,
  FileTextIcon,
  LifeBuoyIcon,
  LightbulbIcon,
  TrendingUpIcon,
  WrenchIcon,
} from 'lucide-react'

import { Box, Link, Stack, Typography, keyframes, useTheme } from '@mui/material'
import { ROUTES } from 'routes'

import { Term } from 'components'
import { AskVeraIcon } from 'components/display/AskVeraIcon/AskVeraIcon'
import { describeCitationAge } from 'ask/marketCitation'
import { findGlossaryTerms, segmentAnswer } from 'ask/glossaryTerms'
import { toolCallLabel } from 'ask/toolLabels'
import { InfoTooltip } from 'components/feedback/InfoTooltip'
import { useGlossaryCorpus } from 'docs/GlossaryProvider'
import { useAskStore } from 'ask'
import type { AskConversation, AskDocCitation, AskMarketCitation, AskMessage, AskToolCall } from 'ask'

import { AskResultView } from './AskResultView'

/**
 * A left-to-right sweep across the text, via `background-position`.
 *
 * PURE CSS keyframes, deliberately not a JS/rAF-driven animation. The Claude Code
 * preview pane fires zero `requestAnimationFrame` and `ResizeObserver` callbacks, so
 * anything built on either can never even START there — a CSS `animation` is the one
 * mechanism whose timeline the pane still advances, and is therefore the only choice
 * that can be verified by scrubbing rather than only by reading the source.
 */
const shimmerSweep = keyframes`
  0%   { background-position: 150% 0; }
  100% { background-position: -50% 0; }
`

/**
 * "Thinking…" — replaces the old 3-line skeleton while a turn has produced no text yet.
 *
 * The mark is STATIC and the text SHIMMERS, not the other way round — deliberately, and
 * not just by default. This indicator fires on every single turn, many times a
 * session; an animating logo wears out fast at that frequency, while the sweep across
 * the word carries the "working" signal without the brand mark itself ever moving.
 */
const ThinkingIndicator = () => {
  const theme = useTheme()
  const base = theme.palette.text.secondary
  const peak = theme.palette.text.primary

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Box sx={{ color: theme.palette.text.secondary, display: 'flex', flexShrink: 0 }}>
        <AskVeraIcon size={16} />
      </Box>
      <Typography
        component="span"
        variant="body2"
        sx={{
          fontWeight: 600,
          backgroundImage: `linear-gradient(90deg, ${base} 0%, ${base} 35%, ${peak} 50%, ${base} 65%, ${base} 100%)`,
          backgroundSize: '250% 100%',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          color: 'transparent',
          WebkitTextFillColor: 'transparent',
          animation: `${shimmerSweep} 1.8s linear infinite`,
          // Reduced motion: a static label in the resting (secondary) colour, no sweep.
          '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
            backgroundImage: 'none',
            color: base,
            WebkitTextFillColor: base,
          },
        }}
      >
        Thinking…
      </Typography>
    </Stack>
  )
}

export const AskMessageList = ({ conversation }: { conversation: AskConversation }) => {
  const bottom = useRef<HTMLDivElement | null>(null)
  const last = conversation.messages[conversation.messages.length - 1]

  // Follow the tail as text streams in. Keyed on the last message's length rather than on
  // the array, so it also fires for deltas appended to a message already in the list.
  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' })
  }, [conversation.messages.length, last?.text.length, last?.status])

  return (
    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 4, py: 4 }}>
      <Stack spacing={5}>
        {conversation.messages.map((m, i) => (
          <MessageRow
            key={m.id}
            message={m}
            // The nearest preceding user question — used only to prefill a feature
            // request when this assistant turn is a `no_data` card (VRG-227). Computed
            // here, not in MessageRow, because MessageRow only ever sees ONE message and
            // has no way to look backward through the conversation itself.
            precedingQuestion={findPrecedingQuestion(conversation.messages, i)}
          />
        ))}
      </Stack>
      <div ref={bottom} />
    </Box>
  )
}

/** The nearest 'user' message strictly before `index`, or '' if there is none. */
const findPrecedingQuestion = (messages: AskMessage[], index: number): string => {
  for (let i = index - 1; i >= 0; i--) {
    if (messages[i].role === 'user') return messages[i].text
  }
  return ''
}

const MessageRow = ({
  message,
  precedingQuestion,
}: {
  message: AskMessage
  precedingQuestion: string
}) => {
  const theme = useTheme()
  // Only used by the "Submit a request" link further down — has to be called up here,
  // above the early `role === 'user'` return, or it would be conditional.
  const startConversation = useAskStore(s => s.startConversation)

  /**
   * Glossary terms this answer used: underlined in the prose, and listed under TERMS.
   *
   * Called HERE, above the `role === 'user'` early return, for the same reason
   * `startConversation` is — a hook below that return runs on assistant rows and not on
   * user rows, which is a Rules of Hooks violation and breaks the whole tree.
   *
   * Matched in code, never by asking the model to tag its own terms: a "glossary term"
   * that is not in the glossary would defeat the single-definition rule this list exists
   * to uphold, and code cannot produce one the corpus does not contain.
   *
   * `useGlossaryCorpus()` fails CLOSED to the brand corpus — that is the safety property.
   * `audience: internal` entries must never surface to a brand reader, and this call site
   * is what decides which corpus is in play. Never swap it for a direct `GLOSSARY` import.
   */
  const corpus = useGlossaryCorpus()
  const isAnswer = message.role === 'assistant' && !!message.text
  const terms = React.useMemo(
    () => (isAnswer ? findGlossaryTerms(message.text, corpus) : []),
    [isAnswer, message.text, corpus],
  )
  // The same match set as renderable spans, so the prose itself can carry the underline.
  const segments = React.useMemo(
    () => (isAnswer ? segmentAnswer(message.text, corpus) : [{ text: message.text }]),
    [isAnswer, message.text, corpus],
  )

  // VRG-464 — split once into the two kinds so each renders in its own, visually
  // distinct block below (VRG-445 AC3). Computed above the `role === 'user'` early
  // return for the same Rules-of-Hooks reason `terms`/`segments` are.
  const docCitations = React.useMemo(
    () => (message.citations ?? []).filter((c): c is AskDocCitation => c.kind !== 'market'),
    [message.citations],
  )
  const marketCitations = React.useMemo(
    () => (message.citations ?? []).filter((c): c is AskMarketCitation => c.kind === 'market'),
    [message.citations],
  )

  if (message.role === 'user') {
    return (
      <Box
        sx={{
          alignSelf: 'flex-end',
          maxWidth: '90%',
          ml: 'auto',
          px: 3,
          py: 2,
          borderRadius: 2,
          bgcolor: theme.palette.brand['600-6%'],
          border: `1px solid ${theme.palette.primary[100]}`,
        }}
      >
        <Typography variant="body2" color="text.primary" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
          {message.text}
        </Typography>
      </Box>
    )
  }

  const waiting = message.status === 'streaming' && message.text.length === 0

  return (
    <Stack spacing={3}>
      {message.toolCalls?.map((call, i) => (
        <React.Fragment key={`${call.name}-${i}`}>
          <ToolCallRow call={call} />
          {/* The rows behind `summary` — "5 rows" is a narration, this is the actual
              data it narrated. Rendered only once the call has a structured result to
              show (VRG-227); a `running`/`error` call has none. */}
          {call.result && <AskResultView result={call.result} />}
        </React.Fragment>
      ))}

      {waiting ? (
        // The "Thinking…" indicator, not a skeleton — a paragraph-shaped skeleton reads
        // as content already partly rendered; a turn that has produced nothing yet
        // should say so instead. See the component for why the mark is static and the
        // text carries the motion.
        <ThinkingIndicator />
      ) : (
        <Typography variant="body2" color="text.primary" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
          {/* Glossary terms are marked in place with the SAME `Term` component a guide's own
              prose uses (`components/feedback/Term`) — not a hand-rolled second copy of its
              styling. That was the bug: this used to underline in `brand['600-32%']` with no
              InfoTooltip/overlay behind it, so a term reference here looked and behaved
              differently from the identical reference inside a doc, which is exactly the
              drift §36/§47 exist to prevent. `Term` already resolves against the reader's own
              corpus (failing closed to plain prose for an id above their floor), so this needs
              no `seg.entry` short-circuit of its own beyond what `segmentAnswer` already did.
              `segmentAnswer` reassembles to the byte-identical original — guarded, because
              off-by-one here would corrupt the ANSWER, not just its styling. */}
          {segments.map((seg, i) =>
            seg.entry ? (
              <Term key={i} id={seg.entry.id}>
                {seg.text}
              </Term>
            ) : (
              <React.Fragment key={i}>{seg.text}</React.Fragment>
            ),
          )}
        </Typography>
      )}

      {message.status === 'error' && (
        <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ color: 'error.main' }}>
          <Box sx={{ display: 'flex', pt: '2px' }}>
            <AlertCircleIcon strokeWidth={1} size={16} />
          </Box>
          <Typography variant="body4" sx={{ lineHeight: 1.5 }}>
            {message.error ?? 'Something went wrong answering that.'}
          </Typography>
        </Stack>
      )}

      {message.status === 'cancelled' && (
        <Typography variant="body3" color="text.secondary">
          Stopped.
        </Typography>
      )}

      {/* Nothing — neither retrieval nor a tool — covered the question, for one of two
          DIFFERENT reasons (VRG-227, split from a single "uncovered" card):
            - no_docs: a how-to question no guide covers. A documentation support request
              is the right next step, same as before the split.
            - no_data: a legitimate question about the caller's own company data, but
              Vera doesn't record the field it needs. A documentation request would never
              be answerable here — no doc can describe a field that doesn't exist — so
              this offers a FEATURE request instead, naming what's missing.
          Both offers are a LINK the user chooses to follow; neither must ever file a
          request on someone's behalf. "We couldn't answer, so we opened a ticket for
          you" is a surprising side effect from a question. */}
      {/*
        Neutral chrome, not a tinted surface. This is an informational dead-end, not a
        brand moment — it used to fill with `primary[50]`, which is a plain neutral grey
        in this palette but still gave the card its OWN raised surface distinct from the
        rest of the panel (which sits directly on the chrome surface it shares with the
        sidebar — see AskPanel's `bgcolor`, which no longer reads `background.paper`).
        Border-only,
        matching the tool-call chip above, is the "neutral surface the rest of the panel
        already uses": no fill of its own, just a hairline.
      */}
      {message.uncovered && (
        <Stack
          spacing={2}
          sx={{
            px: 3,
            py: 3,
            borderRadius: 2,
            border: `1px solid ${theme.palette.primary[100]}`,
          }}
        >
          {message.uncovered.kind === 'no_docs' && (
            <>
              <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                The docs don't cover this one. A person can help.
              </Typography>
              {/* Explicit colour rather than the Link/icon default, so this can never
                  pick up a brand tint from an ancestor — a link the user chooses to
                  follow reads as a normal, neutral affordance (underline + icon), not a
                  call to action. */}
              <Link
                component={RouterLink}
                to={`${ROUTES.SUPPORT_NEW}?type=support`}
                // VRG-227: a support request is a new topic, not a continuation of
                // whatever question the docs couldn't cover. Without this, later
                // activity in the panel kept appending to the same conversation the
                // request was raised from — so its history-list title (still the
                // ORIGINAL unrelated question) stopped describing what the thread
                // actually contained.
                onClick={() => startConversation()}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  color: 'text.primary',
                }}
              >
                <LifeBuoyIcon strokeWidth={1} size={14} />
                <Typography variant="body3" sx={{ fontWeight: 600 }}>
                  Submit a request
                </Typography>
              </Link>
            </>
          )}
          {message.uncovered.kind === 'no_data' && (
            <>
              <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                Vera doesn't currently track {message.uncovered.missing}. Let us know and
                we'll consider adding it.
              </Typography>
              <Link
                component={RouterLink}
                to={`${ROUTES.SUPPORT_NEW}?type=feature&body=${encodeURIComponent(
                  precedingQuestion,
                )}`}
                onClick={() => startConversation()}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  color: 'text.primary',
                }}
              >
                <LightbulbIcon strokeWidth={1} size={14} />
                <Typography variant="body3" sx={{ fontWeight: 600 }}>
                  Suggest a feature
                </Typography>
              </Link>
            </>
          )}
          {/* VRG-464 — a legitimate market question, but market_stats matched no approved
              statistic. Same "offer a link, never act on the user's behalf" shape as the
              two cards above — but NOT "Suggest a feature", which is what this used to
              offer and is the wrong shape entirely. The reader did not ask for a feature;
              they asked a question we cannot answer yet, and the honest response is a way
              to reach a human about it. The copy is also no longer "approved market
              data" — "approved" is our internal workflow leaking into a brand's answer,
              and it means nothing to them. */}
          {message.uncovered.kind === 'no_market_data' && (
            <>
              <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                Vera doesn't have market data on this yet. Get in touch if it's something
                you need and we'll look into it.
              </Typography>
              <Link
                component={RouterLink}
                to={`${ROUTES.SUPPORT_NEW}?type=question&body=${encodeURIComponent(
                  precedingQuestion,
                )}`}
                onClick={() => startConversation()}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  color: 'text.primary',
                }}
              >
                <LifeBuoyIcon strokeWidth={1} size={14} />
                <Typography variant="body3" sx={{ fontWeight: 600 }}>
                  Contact us
                </Typography>
              </Link>
            </>
          )}
        </Stack>
      )}

      {docCitations.length > 0 && (
        <Stack spacing={2}>
          <Typography variant="body3" color="text.secondary" sx={{ fontWeight: 600 }}>
            Sources
          </Typography>
          {docCitations.map(c => (
            <Link
              key={`${c.category}/${c.slug}#${c.headingId ?? ''}`}
              component={RouterLink}
              to={`${ROUTES.DOC(c.category, c.slug)}${c.headingId ? `#${c.headingId}` : ''}`}
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 2, textDecoration: 'none', minWidth: 0 }}
            >
              <FileTextIcon strokeWidth={1} size={14} />
              <Typography variant="body3" noWrap>
                {/* The heading, when retrieval gave us one. Four chunks of a single guide
                    otherwise render as its title four times over, which reads as a bug
                    and tells the reader nothing about which part was used. */}
                {c.headingText ? `${c.title} → ${c.headingText}` : c.title}
              </Typography>
            </Link>
          ))}
        </Stack>
      )}

      {/* VRG-464 — market citations render as a VISIBLY DIFFERENT block, never folded
          into "Sources" above (VRG-445 AC3: a market claim and a doc/SQL fact must never
          be visually interchangeable). A tinted, bordered card per statistic — not a
          plain link row — with an external-link affordance (the destination is a
          third-party article, not a page inside this app) and its own age line (AC4:
          staleness is computed and rendered here, never stated by the model). */}
      {marketCitations.length > 0 && (
        <Stack spacing={2}>
          <Typography variant="body3" color="text.secondary" sx={{ fontWeight: 600 }}>
            Market data
          </Typography>
          {marketCitations.map(c => (
            <MarketCitationCard key={c.id} citation={c} />
          ))}
        </Stack>
      )}

      {terms.length > 0 && (
        <Stack spacing={2}>
          <Typography variant="body3" color="text.secondary" sx={{ fontWeight: 600 }}>
            Terms
          </Typography>
          {/*
            The terms themselves flow in a WRAPPING ROW, not one per line (VRG-227).
            Stacked vertically, two or three one-word links ("Owner", "Scan") each claimed
            a full row of the panel, so a footnote occupied more vertical space than the
            sentence it annotated — and the panel is a narrow column where vertical space
            is the scarce axis. A term is a chip-sized thing; a row of them reads as one
            footnote. `noWrap` stays on each label so a long term truncates rather than
            breaking mid-word and destroying the row rhythm.
          */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, minWidth: 0 }}>
            {/*
              `InfoTooltip`, NOT a bare MUI `Tooltip` + a link that navigates away.

              This row used to hand-roll its own hover: `<Tooltip title={entry.short}>`
              around a `RouterLink` to the glossary page. Two things were wrong with it,
              and they are the same two §44 and §36 already legislate.

              1. The tooltip had NO FOOTER. `InfoTooltip` renders the term's name and a
                 way into the full entry beneath the definition; the bare Tooltip showed
                 a floating paragraph with nothing identifying which term it defined —
                 which is most confusing at exactly the moment it is read, mid-answer.
              2. Clicking LEFT THE PANEL for /docs/reference/glossary#id, abandoning a
                 half-read answer to read one definition. `InfoTooltip` opens the entry
                 IN PLACE via `TermOverlay`, so the answer is still there afterwards.

              The in-prose terms a few lines above already use `Term`, which composes
              `InfoTooltip`. So the SAME term rendered twice in one message behaved two
              different ways depending on whether it appeared in the sentence or in this
              footnote row — the exact drift §36 names, and the second time this file has
              had it (see the `Term` comment above, which records the first).
            */}
            {terms.map(({ entry }) => (
              <InfoTooltip key={entry.id} term={entry.id} placement="top">
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    minWidth: 0,
                    cursor: 'help',
                    color: 'text.secondary',
                  }}
                >
                  <BookOpenIcon strokeWidth={1} size={14} />
                  <Typography variant="body3" noWrap>
                    {entry.term}
                  </Typography>
                </Box>
              </InfoTooltip>
            ))}
          </Box>
        </Stack>
      )}
    </Stack>
  )
}

/**
 * The provenance chip above an answer — "Checked your scans · 42 rows", not
 * "query_scans". Keeping the call's own row count/summary beside the human label is
 * what makes this provenance rather than decoration: the label says WHAT was checked,
 * the count is what makes a downstream number traceable instead of merely asserted.
 * Deliberately quiet — secondary text on a hairline border, not a badge competing with
 * the answer beneath it.
 *
 * VRG-227: `pl` is 0 here, asymmetric with `pr`. The border box itself already shares
 * the answer paragraph's left edge (both are full-width siblings in the same Stack,
 * see `MessageRow`) — the mismatch users actually saw was the WrenchIcon rendering
 * ~24px further right than the paragraph's first character, because this row used to
 * pad its own left side (`px: 3`) before the icon on top of the icon+gap it already
 * carries. The paragraph gets no compensating padding of its own (per the brief — the
 * chip was the one with the stray inset, not the paragraph), so removing this row's
 * left padding is what actually brings the two into agreement.
 */
const ToolCallRow = ({ call }: { call: AskToolCall }) => {
  const theme = useTheme()

  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      sx={{
        pl: 0,
        pr: 3,
        py: 2,
        borderRadius: 2,
        border: `1px solid ${theme.palette.primary[100]}`,
        color: 'text.secondary',
      }}
    >
      <Box sx={{ display: 'flex', flexShrink: 0 }}>
        <WrenchIcon strokeWidth={1} size={14} />
      </Box>
      <Typography variant="body4" color="text.secondary" sx={{ flex: 1, minWidth: 0 }} noWrap>
        {toolCallLabel(call)}
      </Typography>
      <Typography variant="body4" color={call.status === 'error' ? 'error.main' : 'text.secondary'}>
        {call.status === 'running' ? 'running…' : call.error ?? call.summary ?? 'done'}
      </Typography>
    </Stack>
  )
}

/**
 * VRG-464 — one market_stats citation, rendered as a distinct, tinted card (VRG-445
 * AC3). Deliberately NOT the plain doc-citation link row above: `theme.palette.warning`
 * (the same "worth a second look" tint the app already uses elsewhere — e.g.
 * `LinkPackageModal`'s mismatch border, `BulkActivationModal`'s notice) signals "this is
 * a third-party claim, not our own record or our own data" at a glance, before the
 * reader even reads the age line.
 *
 * The link opens the source ARTICLE in a new tab (`target="_blank" rel="noopener
 * noreferrer"`) — an ordinary `<a>`, not a `RouterLink`, because the destination is
 * outside this app entirely. `url` can be null (a stat sourced from something with no
 * public link to paste — see `ask_claims.source_citation`'s own column comment); the
 * card still renders, just without the external-link affordance.
 */
const MarketCitationCard = ({ citation }: { citation: AskMarketCitation }) => {
  const theme = useTheme()
  const age = describeCitationAge(citation)

  const body = (
    <Stack
      spacing={1}
      sx={{
        px: 3,
        py: 2,
        borderRadius: 2,
        bgcolor: theme.palette.warning[50],
        border: `1px solid ${theme.palette.warning[200]}`,
      }}
    >
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Box sx={{ display: 'flex', flexShrink: 0, pt: '2px', color: theme.palette.warning.main }}>
          <TrendingUpIcon strokeWidth={1} size={14} />
        </Box>
        <Stack spacing={1} sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography variant="body3" sx={{ fontWeight: 600, minWidth: 0 }} noWrap>
              {citation.title}
            </Typography>
            {citation.url && (
              <Box sx={{ display: 'flex', flexShrink: 0, color: 'text.secondary' }}>
                <ExternalLinkIcon strokeWidth={1} size={12} />
              </Box>
            )}
          </Stack>
          <Typography variant="body3" color="text.secondary">
            {/* Publication + age together — provenance and staleness in one line, so a
                reader never has one without the other (VRG-445 AC2/AC4). */}
            {[citation.publication, age.label ? `Published ${age.label}` : null]
              .filter(Boolean)
              .join(' · ') || 'Source not recorded'}
          </Typography>
          {age.isStale && (
            <Typography variant="body3" color="warning.main" sx={{ fontWeight: 600 }}>
              This figure may be outdated.
            </Typography>
          )}
        </Stack>
      </Stack>
    </Stack>
  )

  if (!citation.url) return body

  return (
    <Link
      href={citation.url}
      target="_blank"
      rel="noopener noreferrer"
      sx={{ display: 'block', textDecoration: 'none', color: 'inherit' }}
    >
      {body}
    </Link>
  )
}
